import React, { useEffect, useState } from 'react';
import API from '../utils/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function FinancialReport() {
  const [report, setReport] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    API.get(`/admin/financial-report?year=${year}`).then(r => { setReport(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [year]);

  const chartData = report?.monthlyData?.map((d, i) => ({
    month: MONTHS[i],
    Donations: d.donations,
    Expenses: d.totalExpenses,
    'Milk Revenue': d.milkRevenue,
  })) || [];

  const totals = report?.monthlyData?.reduce((acc, d) => ({
    donations: acc.donations + d.donations,
    expenses: acc.expenses + d.totalExpenses,
    milk: acc.milk + d.milkRevenue,
  }), { donations: 0, expenses: 0, milk: 0 }) || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 className="admin-page-title" style={{ margin: 0 }}>📈 Financial Report</h2>
        <select className="form-select" style={{ width: 110 }} value={year} onChange={e => setYear(e.target.value)}>
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading ? <div className="loader"><div className="spinner" /></div> : !report ? <p>Failed to load report</p> : (
        <>
          {/* Annual Summary Cards */}
          <div className="stats-cards" style={{ marginBottom: 32 }}>
            {[
              { label: `Total Donations ${year}`, value: `₹${totals.donations?.toLocaleString('en-IN')}`, color: 'saffron' },
              { label: `Total Expenses ${year}`, value: `₹${totals.expenses?.toLocaleString('en-IN')}`, color: 'brown' },
              { label: `Milk Revenue ${year}`, value: `₹${totals.milk?.toLocaleString('en-IN')}`, color: 'gold' },
              { label: 'Net Surplus', value: `₹${((totals.donations + totals.milk - totals.expenses) || 0).toLocaleString('en-IN')}`, color: 'green' },
            ].map((s, i) => (
              <div key={i} className={`stat-card ${s.color}`}>
                <div className="stat-card-label">{s.label}</div>
                <div className="stat-card-value">{s.value}</div>
              </div>
            ))}
          </div>

          {/* Chart */}
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 16px rgba(0,0,0,0.08)', marginBottom: 32 }}>
            <h3 style={{ fontWeight: 700, marginBottom: 20 }}>Monthly Overview — {year}</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={v => `₹${v?.toLocaleString('en-IN')}`} />
                <Legend />
                <Bar dataKey="Donations" fill="#FF9933" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Expenses" fill="#8B4513" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Milk Revenue" fill="#138808" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Monthly Breakdown Table */}
          <div className="admin-table">
            <table>
              <thead><tr>
                <th>Month</th><th>Donations</th><th>Milk Revenue</th><th>Dry Grass</th><th>Wet Grass</th><th>Medical</th><th>Salary</th><th>Other</th><th>Total Expenses</th><th>Net</th>
              </tr></thead>
              <tbody>
                {report.monthlyData.map((d, i) => {
                  const net = d.donations + d.milkRevenue - d.totalExpenses;
                  return (
                    <tr key={i}>
                      <td style={{ fontWeight: 600 }}>{MONTHS[i]}</td>
                      <td style={{ color: 'var(--saffron)', fontWeight: 600 }}>₹{d.donations.toLocaleString('en-IN')}</td>
                      <td style={{ color: 'var(--green)' }}>₹{d.milkRevenue.toLocaleString('en-IN')}</td>
                      <td>₹{d.dryGrass.toLocaleString('en-IN')}</td>
                      <td>₹{d.wetGrass.toLocaleString('en-IN')}</td>
                      <td>₹{d.medical.toLocaleString('en-IN')}</td>
                      <td>₹{d.salary.toLocaleString('en-IN')}</td>
                      <td>₹{d.other.toLocaleString('en-IN')}</td>
                      <td style={{ color: '#8B4513', fontWeight: 600 }}>₹{d.totalExpenses.toLocaleString('en-IN')}</td>
                      <td style={{ fontWeight: 700, color: net >= 0 ? 'var(--green)' : '#c62828' }}>
                        {net >= 0 ? '+' : ''}₹{net.toLocaleString('en-IN')}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
